import React, { useState, useEffect } from "react";
import {
  FaBars,
  FaWallet,
  FaUser,
  FaLanguage,
  FaSignOutAlt,
  FaTimes,
  FaHome,
} from "react-icons/fa";
import { IoIosArrowDown } from "react-icons/io";
import { MdAccountBalance, MdSwitchAccount } from "react-icons/md";
import { GiTicket } from "react-icons/gi";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearWallet, fetchWalletData } from "../../redux/slices/walletSlice";
import { logout } from "../../redux/slices/authSlice";
import { clearTickets } from "../../redux/slices/purchaseTicketsSlice";

const Header = () => {
  // Localization hooks
  const { t, i18n } = useTranslation();

  // Component state
  const [menuOpen, setMenuOpen] = useState(false);
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const [accountMenuOpen, setAccountMenuOpen] = useState(false);

  // Redux state selectors
  const { balance } = useSelector((state) => state.wallet);
  const id = localStorage.getItem("userID");

  // Navigation and state management
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // State management handlers
  const toggleMenu = () => setMenuOpen((prev) => !prev);
  const toggleLanguageMenu = () => setLanguageMenuOpen((prev) => !prev);
  const toggleAccountMenu = () => setAccountMenuOpen((prev) => !prev);

  // Language handling
  const changeLanguage = (language) => {
    i18n.changeLanguage(language);
    localStorage.setItem("selectedLanguage", language);
  };

  // Logout handler with state cleanup
  const handleLogout = () => {
    // Save the selected language value
    const selectedLanguage = localStorage.getItem("selectedLanguage");

    // Clear the local storage
    localStorage.clear();

    // Restore the selected language value
    if (selectedLanguage) {
      localStorage.setItem("selectedLanguage", selectedLanguage);
    }

    // Dispatch the logout action
    dispatch(logout());
    dispatch(clearTickets());
    dispatch(clearWallet());

    // Navigate to the login page
    navigate("/login");
  };

  // Navigation handlers with menu closure
  const navigationHandlers = {
    profile: () => navigateWithMenuClose("/profile"),
    wallet: () => navigateWithMenuClose("/wallet"),
    ticketDetails: () => navigateWithMenuClose("/ticket-details"),
    home: () => navigateWithMenuClose("/"),
    ticketHistory: () => navigateWithMenuClose("/tickets-history"),
    accountDetails: () => navigateWithMenuClose("/lottery-account"),
  };

  const navigateWithMenuClose = (path) => {
    navigate(path);
    setMenuOpen(false);
  };

  // Effect for initial language setup
  useEffect(() => {
    const savedLanguage = localStorage.getItem("selectedLanguage");
    if (savedLanguage) i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  // Effect for wallet data fetching
  useEffect(() => {
    if (id) {
      dispatch(fetchWalletData(id));
    }
  }, [id, dispatch]);

  return (
    <header
      className="flex items-center justify-between px-6 py-2"
      style={{ position: "relative", zIndex: 999 }}
    >
      {/* Fullscreen Blur Background */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-30 backdrop-blur-sm"
          style={{ zIndex: 998 }}
          onClick={toggleMenu}
        ></div>
      )}

      {/* Left Menu Button */}
      <div className="relative">
        <button
          onClick={toggleMenu}
          className="text-white text-2xl px-1 pt-8 rounded-full focus:outline-none transition"
          aria-label="Open Menu"
        >
          <FaBars />
        </button>

        {menuOpen && (
          <div
            className="absolute top-14 left-0 bg-[#0A0A0A] text-white w-64 rounded-lg shadow-lg animate-fadeIn"
            style={{
              boxShadow:
                "0px 4px 15px #EBC377, 0px -4px 15px #EBC377, 4px 0px 15px #EBC377, -4px 0px 15px #EBC377",
              zIndex: 999,
            }}
          >
            {/* Menu header with close button */}
            <div className="flex flex-row justify-between p-2 mb-3">
              <div className="flex justify-start ml-3">
                <button
                  onClick={navigationHandlers.home}
                  className="text-blue-500 text-lg hover:text-blue-700 transition"
                  aria-label="Home"
                  label={t("header.home")}
                >
                  <FaHome />
                </button>
              </div>
              <div className="flex justify-end ">
                <button
                  onClick={toggleMenu}
                  className="text-red-500 text-lg hover:text-red-700 transition"
                  aria-label="Close Menu"
                >
                  <FaTimes />
                </button>
              </div>
            </div>

            {/* Menu items */}
            <ul className="mb-3">
              {/* <MenuItem
                icon={
                  <FaHome className="text-blue-500 text-lg hover:text-blue-700 transition" />
                }
                label={t("header.home")}
                onClick={handleHome}
              /> */}
              <li
                className="flex items-center justify-between px-5 py-3 hover:bg-gradient-to-r from-[#C38B20] to-[#E5AA36] cursor-pointer transition"
                onClick={toggleAccountMenu}
              >
                <div className="flex items-center gap-3">
                  <MdSwitchAccount className="text-purple-500" />
                  {t("header.account")}
                </div>
                <IoIosArrowDown
                  className={`text-gray-500 transition-transform ${
                    accountMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </li>
              {accountMenuOpen && (
                <ul className="ml-6 mt-1 border-l-2 border-gray-300">
                  <SubMenuItem
                    icon={<FaUser className="text-blue-500" />}
                    label={t("header.profile")}
                    onClick={navigationHandlers.profile}
                  />
                  <SubMenuItem
                    label={t("header.ticketDetails")}
                    icon={<GiTicket className="text-blue-500" />}
                    onClick={navigationHandlers.ticketDetails}
                  />
                  <SubMenuItem
                    label={t("header.ticketHistory")}
                    icon={<GiTicket className="text-blue-500" />}
                    onClick={navigationHandlers.ticketHistory}
                  />
                  <SubMenuItem
                    label={t("header.accountDetails")}
                    icon={<MdAccountBalance className="text-blue-500" />}
                    onClick={navigationHandlers.accountDetails}
                  />
                </ul>
              )}

              {/* Wallet menu item */}
              <MenuItem
                icon={<FaWallet className="text-green-500" />}
                label={t("header.wallet")}
                onClick={navigationHandlers.wallet}
              />
              <li
                className="flex items-center justify-between px-5 py-3 hover:bg-gradient-to-r from-[#C38B20] to-[#E5AA36] cursor-pointer transition"
                onClick={toggleLanguageMenu}
              >
                <div className="flex items-center gap-3">
                  <FaLanguage className="text-purple-500" />
                  {t("header.language")}
                </div>
                <IoIosArrowDown
                  className={`text-gray-500 transition-transform ${
                    languageMenuOpen ? "rotate-180" : ""
                  }`}
                />
              </li>

              {/* Language submenu */}
              {languageMenuOpen && (
                <ul className="ml-6 mt-1 border-l-2 border-gray-300">
                  <SubMenuItem
                    label={t("header.hindi")}
                    flag="🇮🇳"
                    onClick={() => changeLanguage("hi")}
                  />
                  <SubMenuItem
                    label={t("header.english")}
                    flag="🇺🇸"
                    onClick={() => changeLanguage("en")}
                  />
                </ul>
              )}

              {/* Logout button */}
              <MenuItem
                icon={<FaSignOutAlt className="text-red-500" />}
                label={t("header.logout")}
                onClick={handleLogout}
              />
            </ul>
          </div>
        )}
      </div>

      {/* Wallet display */}
      <button
        className="text-white text-2xl mr-[-7px] rounded-full focus:outline-none transition"
        onClick={navigationHandlers.wallet}
        aria-label="Go to Wallet"
      >
        <div className="absolute right-12 top-9 text-white lg:text-[12px] text-sm font-inter font-bold bg-green-700 px-2 rounded-full">
          {`₹ ${balance}`}
        </div>
        <img
          src="images/wallet.gif"
          alt="Wallet animation"
          className="w-16 h-16"
        />
      </button>
    </header>
  );
};

// Reusable Menu Item Component
const MenuItem = ({ icon, label, onClick }) => (
  <li
    className="flex items-center gap-3 px-5 py-3 hover:bg-gradient-to-r from-[#C38B20] to-[#E5AA36] cursor-pointer transition"
    onClick={onClick}
  >
    {icon}
    {label}
  </li>
);

// Reusable Submenu Item Component
const SubMenuItem = ({ label, flag, onClick, icon }) => (
  <li
    className="px-5 py-2 hover:bg-gradient-to-r from-[#C38B20] to-[#E5AA36] cursor-pointer transition flex items-center gap-2"
    onClick={onClick}
  >
    {flag}
    {icon} {label}
  </li>
);

export default Header;
